import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ViewChild,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { ngForFadeInOpacity, ngForFadeInOut } from 'src/app/animations';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Observable, forkJoin, of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import {
  DigitalPublicationSection,
  IDigitalPublicationSection,
} from 'src/app/core/dtos/interfaces/i-digital-publication-section';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { IDigitalPublicationSectionGroup } from '../../interfaces/i-digital-publication-section-group';
import { DigitalPublicationSectionEvent } from 'src/app/core/dtos/interfaces/i-digital-publication-section-event';
import { IManager } from 'src/app/core/models/i-manager';
import { ManagerService } from 'src/app/shared/services/manager.service';
import { IManagerResponse } from 'src/app/core/models/i-manager-response';
import { RELATED_MANAGER_FORM_PATHS } from '../related-manager-item/constants/form-paths';
import { RelatedManager } from 'src/app/core/models/i-related-manager';
import { emit } from 'process';

@Component({
  selector: 'app-digital-publication-section',
  templateUrl: './digital-publication-section.component.html',
  styleUrls: ['./digital-publication-section.component.scss'],
  animations: [ngForFadeInOpacity, ngForFadeInOut],
})
export class DigitalPublicationSectionComponent implements OnInit, OnChanges {
  @Input() section: IDigitalPublicationSection;
  @Input() index: number;
  @Input() changingSectionOrder$: Observable<boolean>;
  @Input() isDisabled = false;
  @Input() urlPath = '';

  @Input() sectionErrors: ValidationErrors = {};

  @Output() sectionOrderChanged = new EventEmitter<number[]>();
  @Output() sectionChanged = new EventEmitter<DigitalPublicationSectionEvent>();
  @Output() removeSectionClick = new EventEmitter<number>();

  @ViewChild(MatExpansionPanel) matExpansionPanel: MatExpansionPanel;

  title = 'Section';
  iconClass: string;
  isExpandedByDefault = true;
  isExpandable = true;
  isExpanded = false;

  HEADER_HEIGHT = '43px';
  maxNameLength = 66;

  form = new FormGroup<IDigitalPublicationSectionGroup>({
    id: new FormControl(),
    order: new FormControl(),
    name: new FormControl('', Validators.required),
    path: new FormControl(''),
    relatedManagers: this.formBuilder.array([]),
    relatedAssetClasses: this.formBuilder.array([]),
    relatedSectors: this.formBuilder.array([]),
    relatedFunds: this.formBuilder.array([]),
    relatedEtfs: this.formBuilder.array([]),
    relatedInvestmentTrusts: this.formBuilder.array([]),
    relatedShares: this.formBuilder.array([]),
    relatedCompanies: this.formBuilder.array([]),
    relatedPeoples: this.formBuilder.array([]),
    tags: this.formBuilder.array([]),
    topics: this.formBuilder.array([]),
  });

  surtitleControl = this.formBuilder.array([]);

  constructor(
    public _dialogService: DialogService,
    public managerService: ManagerService,
    public formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initFormChanges();
  }

  onOpen(_): void {
    this.isExpanded = true;
  }

  onClose(_): void {
    this.isExpanded = false;
  }

  openPanel() {
    if (this.isExpandable) {
      this.matExpansionPanel.open();
    }
  }

  private initForm(): void {
    if (this.isDisabled) {
      this.form.disable();
    }

    this.form.controls.id.setValue(this.section.id);
    this.form.controls.order.setValue(this.section.order);
    this.form.controls.name.setValue(this.section.name);
    this.form.controls.path.setValue(this.section.path);

    this.initFormArray(this.section.relatedSectors, 'relatedSectors');
    this.initFormArray(this.section.relatedFunds, 'relatedFunds');
    this.initFormArray(this.section.relatedAssetClass, 'relatedAssetClasses');
    this.initFormArray(this.section.relatedEtfs, 'relatedEtfs');
    this.initFormArray(
      this.section.relatedInvestmentTrusts,
      'relatedInvestmentTrusts'
    );
    this.initFormArray(this.section.relatedShares, 'relatedShares');
    this.initFormArray(this.section.relatedCompanies, 'relatedCompanies');
    this.initFormArray(this.section.relatedPeoples, 'relatedPeoples');
    this.initFormArray(this.section.tags, 'tags');
    this.initFormArray(this.section.topics, 'topics');

    this.initManagersControl();
  }

  private initFormChanges(): void {
    this.form.controls.order.valueChanges.subscribe({
      next: (value) => {
        if (value > 0) {
          this.sectionOrderChanged.emit([value, this.section.order]);
        }
      },
    });

    this.form.valueChanges.subscribe({
      next: (value) => {
        var changedSection = new DigitalPublicationSection(
          value.id,
          value.order,
          value.name,
          value.path,
          this.buildRelatedManagers(this.form.controls.relatedManagers),
          value.relatedAssetClasses,
          value.relatedSectors,
          value.relatedFunds,
          value.relatedEtfs,
          value.relatedInvestmentTrusts,
          value.relatedShares,
          value.relatedCompanies,
          value.relatedPeoples,
          value.tags,
          value.topics
        );
        this.sectionChanged.emit(
          new DigitalPublicationSectionEvent(this.index, changedSection)
        );
      },
    });
  }

  ngOnChanges(): void {
    this.form.controls.path.setErrors(this.sectionErrors, { emitEvent: false });
    this.form.controls.order.setValue(this.section.order, { emitEvent: false });
  }

  initFormArray(array: any[], controlName: string) {
    array?.forEach((item) => {
      this.form.controls[controlName].push(new FormControl(item));
    });
  }

  initManagersControl(): void {
    if (this.section.relatedManagers) {
      forkJoin(
        this.section.relatedManagers.map((manager: IManager) =>
          this.fetchSectorsForManager(manager)
        )
      ).subscribe({
        next: (updatedManagers: IManager[]) => {
          updatedManagers.forEach((manager) => {
            this.form.controls.relatedManagers.push(
              this.formBuilder.group({
                [RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]: manager,
                [RELATED_MANAGER_FORM_PATHS.SELECTED_SECTOR()]:
                  manager.selectedSector,
              }),
              { emitEvent: false }
            );
          });
          this.form.controls.relatedManagers.markAsPristine();
        },
      });
    }
  }

  private fetchSectorsForManager(manager: IManager): Observable<IManager> {
    return this.managerService.getManagerById$(manager.id).pipe(
      mergeMap((managerResponse: IManagerResponse) => {
        manager.sectors = managerResponse.sectors;
        manager.rating = managerResponse.rating;
        return of(manager);
      })
    );
  }

  handleInput(event: KeyboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  removeSection(event: KeyboardEvent): void {
    event.stopPropagation();
    this._dialogService
      .openConfirmationDialog(
        {
          title: 'Delete Section?',
          message: 'Are you sure you want to delete this section?',
          confirmLabel: 'Yes',
        },
        {
          top: 'calc(80px + 5%)',
        }
      )
      .afterClosed()
      .pipe(filter((dialogResponse) => !!dialogResponse))
      .subscribe({
        next: () => {
          this.removeSectionClick.emit(this.section.order - 1);
        },
      });
  }

  buildRelatedManagers(managers: FormArray<FormGroup>): IManager[] {
    let relatedManagers = managers.controls.map((formGroup) => {
      let selectedSectorControl = formGroup.get(
        RELATED_MANAGER_FORM_PATHS.SELECTED_SECTOR()
      );
      let managerControl = formGroup.get(
        RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()
      );
      return new RelatedManager(
        managerControl.value,
        selectedSectorControl.value
      );
    });

    return this.managerService.mapRelatedManagersToManager(relatedManagers);
  }
}
